// src/runtime/ProductRuntimes/BaseProductRuntime.ts
// ============================================
// BaseProductRuntime - 产品 Runtime 抽象基类
//
// 职责：
//   1. 提供 Catalog 元信息（静态 + Vault 动态增强）
//   2. 实现 launch / open_dashboard / refresh 执行命令
//   3. 复用 IRuntime 接口，无需 RuntimeManager 改造
//
// 子类只需实现 buildCatalog() 返回静态 Catalog 元信息，
// 其余通用逻辑（Vault 读取、launch handle 生成）由基类处理。
//
// 设计原则：
//   - Vault 不可达 / 文件不存在时降级到静态 Catalog（不抛异常）
//   - 浏览器环境通过 /vault-api/* 中间件读取，Node 环境通过 fs 读取
//   - 所有 query/execute 返回 ProductResult，前端按 success 判断
// ============================================

import {
  type IRuntime,
  type RuntimeName,
  type RuntimeStatus,
  type RuntimeInfo,
  type RuntimeEvent,
  getHealthFromStatus,
} from '../../types/runtime';
import type {
  ProductCatalogInfo,
  ProductQuery,
  ProductCommand,
  ProductLaunchHandle,
  ProductResult,
} from './types';

/**
 * Vault frontmatter 解析结果
 */
interface ParsedFrontmatter {
  title?: string;
  status?: string;
  progress?: number;
  owner?: string;
  expectedRevenue?: number;
  riskLevel?: string;
  updatedAt?: string;
  milestones?: Array<{ name: string; dueDate: string; status: string }>;
}

/**
 * 浏览器环境判断
 */
const isBrowser =
  typeof window !== 'undefined' && typeof fetch === 'function';

/**
 * 从 Markdown 内容解析 frontmatter
 *
 * 简化版 YAML 解析，仅支持扁平 key: value 和数组。
 * 复杂嵌套场景由 EnterpriseRuntime.VaultWorkspace 处理，此处不重复。
 */
function parseFrontmatter(content: string): ParsedFrontmatter {
  const result: ParsedFrontmatter = {};
  const fmMatch = content.match(/^---\n([\s\S]*?)\n---/);
  if (!fmMatch) return result;
  const fm = fmMatch[1];

  const titleMatch = fm.match(/^title:\s*(.+)$/m);
  if (titleMatch) result.title = titleMatch[1].trim().replace(/^["']|["']$/g, '');

  const statusMatch = fm.match(/^status:\s*(.+)$/m);
  if (statusMatch) result.status = statusMatch[1].trim().replace(/^["']|["']$/g, '');

  const progressMatch = fm.match(/^progress:\s*(\d+(?:\.\d+)?)/m);
  if (progressMatch) result.progress = Number(progressMatch[1]);

  const ownerMatch = fm.match(/^owner:\s*(.+)$/m);
  if (ownerMatch) result.owner = ownerMatch[1].trim().replace(/^["']|["']$/g, '');

  const revenueMatch = fm.match(/^(?:expectedRevenue|revenue):\s*(\d+(?:\.\d+)?)/m);
  if (revenueMatch) result.expectedRevenue = Number(revenueMatch[1]);

  const riskMatch = fm.match(/^riskLevel:\s*(.+)$/m);
  if (riskMatch) result.riskLevel = riskMatch[1].trim().replace(/^["']|["']$/g, '');

  const updatedMatch = fm.match(/^(?:updated|updatedAt):\s*(.+)$/m);
  if (updatedMatch) result.updatedAt = updatedMatch[1].trim().replace(/^["']|["']$/g, '');

  // milestones 数组解析（支持 `- name: xxx` 形式）
  const msSection = fm.match(/^milestones:\n((?:-\s+.+\n?)+)/m);
  if (msSection) {
    const items: Array<{ name: string; dueDate: string; status: string }> = [];
    const itemRegex = /-\s+name:\s*(.+?)(?:,\s*dueDate:\s*(.+?))?(?:,\s*status:\s*(.+?))?$/gm;
    let m: RegExpExecArray | null;
    while ((m = itemRegex.exec(msSection[1])) !== null) {
      items.push({
        name: m[1]?.trim() || '',
        dueDate: m[2]?.trim() || '',
        status: m[3]?.trim() || 'pending',
      });
    }
    if (items.length > 0) result.milestones = items;
  }

  return result;
}

/**
 * 通过 /vault-api 中间件读取 Vault 文件内容（浏览器环境）
 */
async function readVaultFile(vaultPath: string): Promise<string | null> {
  if (!isBrowser || !vaultPath) return null;
  try {
    const resp = await fetch(
      `/vault-api/read?path=${encodeURIComponent(vaultPath)}`,
      { headers: { Accept: 'application/json' } }
    );
    if (!resp.ok) return null;
    const json = await resp.json();
    if (!json?.success || typeof json.content !== 'string') return null;
    return json.content;
  } catch {
    return null;
  }
}

// ============================================
// 抽象基类
// ============================================

export abstract class BaseProductRuntime implements IRuntime {
  abstract readonly name: RuntimeName;
  status: RuntimeStatus = 'loading';
  protected startTime: number = Date.now();
  protected version: string = '1.0.0';
  /** 缓存的 Catalog 数据（静态 + Vault 增强） */
  private cachedCatalog: ProductCatalogInfo | null = null;

  // ============================================
  // 子类必须实现：返回静态 Catalog 元信息
  // ============================================
  protected abstract buildCatalog(): ProductCatalogInfo;

  // ============================================
  // 生命周期
  // ============================================
  async initialize(): Promise<void> {
    try {
      this.status = 'loading';
      // 预加载 Catalog（尝试从 Vault 增强）
      this.cachedCatalog = await this.loadCatalog();
      this.status = 'ready';
      console.log(`[${this.name}] Initialized (catalog loaded)`);
    } catch (error) {
      this.status = 'error';
      console.error(`[${this.name}] Initialization failed:`, error);
      // 失败也降级到静态 Catalog
      this.cachedCatalog = this.buildCatalog();
    }
  }

  async destroy?(): Promise<void> {
    this.status = 'stopped';
    this.cachedCatalog = null;
  }

  // ============================================
  // 加载 Catalog（静态 + Vault 增强）
  // ============================================
  private async loadCatalog(): Promise<ProductCatalogInfo> {
    const base = this.buildCatalog();
    if (!base.vaultPath) return base;

    // 尝试从 Vault 读取真实进度/里程碑
    const content = await readVaultFile(base.vaultPath);
    if (!content) {
      // Vault 不可达或文件不存在，降级到静态
      return { ...base, updatedAt: new Date().toISOString() };
    }

    const fm = parseFrontmatter(content);

    // 白名单校验：防止 frontmatter 脏数据导致前端查表返回 undefined 后崩溃
    const VALID_STATUS = ['running', 'open', 'beta', 'planning', 'paused'] as const;
    const VALID_RISK = ['low', 'medium', 'high'] as const;
    const VALID_MILESTONE_STATUS = ['pending', 'in_progress', 'completed', 'delayed'] as const;

    return {
      ...base,
      displayName: fm.title || base.displayName,
      runtimeStatus: VALID_STATUS.includes(fm.status as any)
        ? (fm.status as ProductCatalogInfo['runtimeStatus'])
        : base.runtimeStatus,
      progress: typeof fm.progress === 'number' ? fm.progress : base.progress,
      owner: fm.owner || base.owner,
      expectedRevenue: typeof fm.expectedRevenue === 'number' ? fm.expectedRevenue : base.expectedRevenue,
      riskLevel: VALID_RISK.includes(fm.riskLevel as any)
        ? (fm.riskLevel as ProductCatalogInfo['riskLevel'])
        : base.riskLevel,
      updatedAt: fm.updatedAt || new Date().toISOString(),
      milestones:
        Array.isArray(fm.milestones) && fm.milestones.length > 0
          ? fm.milestones.slice(0, 5).map((m) => ({
              name: m.name,
              dueDate: m.dueDate,
              status: VALID_MILESTONE_STATUS.includes(m.status as any)
                ? (m.status as ProductCatalogInfo['milestones'][number]['status'])
                : 'pending',
            }))
          : base.milestones,
    };
  }

  // ============================================
  // Query 接口
  // ============================================
  async query<T = unknown>(params?: ProductQuery): Promise<ProductResult<T>> {
    const timestamp = new Date().toISOString();
    const productId = String(this.name);

    try {
      const queryType = params?.type || 'catalog';

      if (queryType === 'catalog') {
        if (!this.cachedCatalog) {
          this.cachedCatalog = await this.loadCatalog();
        }
        return {
          success: true,
          data: this.cachedCatalog as unknown as T,
          productId,
          timestamp,
        };
      }

      if (queryType === 'project_info') {
        if (!this.cachedCatalog) {
          this.cachedCatalog = await this.loadCatalog();
        }
        return {
          success: true,
          data: {
            productId: this.cachedCatalog.productId,
            displayName: this.cachedCatalog.displayName,
            milestones: this.cachedCatalog.milestones,
            capabilities: this.cachedCatalog.capabilities,
            progress: this.cachedCatalog.progress,
            vaultPath: this.cachedCatalog.vaultPath,
          } as unknown as T,
          productId,
          timestamp,
        };
      }

      if (queryType === 'status') {
        return {
          success: true,
          data: {
            productId,
            status: this.status,
            health: getHealthFromStatus(this.status),
            version: this.version,
            uptime: Math.floor((Date.now() - this.startTime) / 1000),
          } as unknown as T,
          productId,
          timestamp,
        };
      }

      return {
        success: false,
        error: `Unknown query type: ${queryType}`,
        productId,
        timestamp,
      };
    } catch (err: any) {
      return {
        success: false,
        error: err?.message || String(err),
        productId,
        timestamp,
      };
    }
  }

  // ============================================
  // Execute 接口
  // ============================================
  async execute<T = unknown>(
    command: ProductCommand,
    params?: Record<string, unknown>
  ): Promise<ProductResult<T>> {
    const timestamp = new Date().toISOString();
    const productId = String(this.name);

    try {
      if (command === 'launch' || command === 'open_dashboard') {
        if (!this.cachedCatalog) {
          this.cachedCatalog = await this.loadCatalog();
        }
        const handle: ProductLaunchHandle = {
          productId,
          launchMode: 'internal',
          target: this.cachedCatalog.launchUrl,
          launchedAt: timestamp,
        };
        return {
          success: true,
          data: handle as unknown as T,
          productId,
          timestamp,
        };
      }

      if (command === 'refresh') {
        // 清缓存并重新加载
        this.cachedCatalog = null;
        this.cachedCatalog = await this.loadCatalog();
        return {
          success: true,
          data: this.cachedCatalog as unknown as T,
          productId,
          timestamp,
        };
      }

      return {
        success: false,
        error: `Unknown command: ${command}`,
        productId,
        timestamp,
      };
    } catch (err: any) {
      return {
        success: false,
        error: err?.message || String(err),
        productId,
        timestamp,
      };
    }
  }

  // ============================================
  // RuntimeInfo
  // ============================================
  getInfo(): RuntimeInfo {
    const health = getHealthFromStatus(this.status);
    const catalog = this.cachedCatalog || this.buildCatalog();
    return {
      name: this.name,
      status: this.status,
      health,
      version: this.version,
      description: catalog.description,
      uptime: Math.floor((Date.now() - this.startTime) / 1000),
    };
  }

  // ============================================
  // 事件处理
  // ============================================
  onEvent(event: RuntimeEvent): void {
    // 预留：可监听 dataUpdated 事件触发 Catalog 重新加载
    if (event.type === 'data_updated' && event.target === this.name) {
      this.cachedCatalog = null;
    }
  }
}
